import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowRight, ExternalLink } from 'lucide-react';
import ExpandableAIChatTable from './ExpandableAIChatTable';
import './BriefingCardsRenderer.css';

// Build the pipe-string rows + numeric-column set that ExpandableAIChatTable expects
// from our structured { columns, rows, totalRow } details shape.
function buildTableProps(details) {
  if (!details || !Array.isArray(details.columns) || !Array.isArray(details.rows)) return null;
  const headerRow = '| ' + details.columns.join(' | ') + ' |';
  const dataRows = details.rows.map(r => '| ' + r.map(c => (c == null ? '' : String(c))).join(' | ') + ' |');
  const totalRows = Array.isArray(details.totalRow) && details.totalRow.length
    ? ['| ' + details.totalRow.map(c => (c == null ? '' : String(c))).join(' | ') + ' |']
    : [];
  const headerCount = details.columns.length;
  const numericRegex = /^\s*[-−+]?\$?[\d,]+(?:\.\d+)?%?\s*$/;
  const numericCols = new Set();
  for (const r of details.rows) {
    r.forEach((c, i) => {
      if (numericRegex.test(String(c == null ? '' : c).replace(/\*\*/g, '').trim())) numericCols.add(i);
    });
  }
  const tableMarkdown = [headerRow, ...dataRows, ...totalRows].join('\n');
  return { headerRow, dataRows, totalRows, headerCount, numericCols, tableMarkdown };
}

const trendStyles = {
  up:      { bg: 'rgba(18, 183, 106, 0.15)',  fg: '#3FCD8B' },
  down:    { bg: 'rgba(240, 68, 56, 0.15)',   fg: '#F58A82' },
  warning: { bg: 'rgba(247, 144, 9, 0.15)',   fg: '#FFB547' },
  neutral: { bg: 'rgba(255, 255, 255, 0.06)', fg: 'var(--lodge-onyx-300)' },
};

// Render narrative with every signed numeric token highlighted as a pill.
// + prefix → green "up" pill, - or − prefix → red "down" pill.
// Unsigned numbers (e.g. "$47,341", "92.7%") render as plain text.
// The metric.label's primary token is always pilled in the trend color even if unsigned,
// so each card has at least one visual anchor matching the accent border.
function renderNarrative(narrative, metric) {
  if (!narrative) return null;

  // Find all signed numeric tokens: +$12,403 | -7 | −$542 | +99.6% | -6.2% | +8.4 pts
  // Negative lookbehind on digit avoids pilling hyphens inside dates like 2026-04-17.
  const SIGNED_TOKEN = /(?<![0-9])[+\-−]\$?\d[\d,]*(?:\.\d+)?%?(?:\s*pts?)?/g;
  const matches = [];
  let m;
  while ((m = SIGNED_TOKEN.exec(narrative)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, text: m[0], isUp: m[0][0] === '+' });
  }

  // Also pill the metric.label's primary number (even if unsigned) — the headline anchor.
  const metricMatch = metric?.label?.match(/~?[+\-−]?\$?[\d,]+(?:\.\d+)?%?/);
  if (metricMatch) {
    const token = metricMatch[0];
    const idx = narrative.indexOf(token);
    if (idx !== -1 && !matches.some(x => x.start === idx)) {
      const trend = metric.trend || 'neutral';
      matches.push({
        start: idx,
        end: idx + token.length,
        text: token,
        trend,
      });
    }
  }

  if (matches.length === 0) return narrative;
  matches.sort((a, b) => a.start - b.start);

  const parts = [];
  let cursor = 0;
  let key = 0;
  for (const mt of matches) {
    if (mt.start < cursor) continue; // skip overlaps
    if (mt.start > cursor) parts.push(narrative.slice(cursor, mt.start));
    const style = mt.trend
      ? (trendStyles[mt.trend] || trendStyles.neutral)
      : (mt.isUp ? trendStyles.up : trendStyles.down);
    parts.push(
      <span key={key++} className="briefing-metric-pill" style={{ background: style.bg, color: style.fg }}>
        {mt.text}
      </span>
    );
    cursor = mt.end;
  }
  if (cursor < narrative.length) parts.push(narrative.slice(cursor));
  return parts;
}

function CardActions({ actions, onPromptClick }) {
  if (!actions?.length) return null;
  return (
    <div className="briefing-card-actions">
      <span className="briefing-actions-label">Actions</span>
      {actions.map((a, i) => {
        if (a.type === 'link' && a.href) {
          const isExternal = a.href.startsWith('http');
          return (
            <a
              key={i}
              href={a.href}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              className="briefing-action-btn"
            >
              {a.label}
              {isExternal ? <ExternalLink size={11} /> : <ArrowRight size={11} />}
            </a>
          );
        }
        // Type 'prompt' — re-prompt the AI as a follow-up
        return (
          <button
            key={i}
            type="button"
            className="briefing-action-btn"
            onClick={() => onPromptClick && onPromptClick(a.prompt)}
          >
            {a.label}
            <ArrowRight size={11} />
          </button>
        );
      })}
    </div>
  );
}

function BriefingCard({ card, onPromptClick, hotelName, isDarkMode }) {
  const portfolioTable = buildTableProps(card.details);
  const locationTable = buildTableProps(card.details?.byLocation);
  const [expanded, setExpanded] = useState(card.defaultExpanded === true);
  const [viewMode, setViewMode] = useState('portfolio'); // 'portfolio' | 'location'
  const activeTable = viewMode === 'location' && locationTable ? locationTable : portfolioTable;
  const hasLocationView = !!locationTable;
  const hasDetails = !!(
    portfolioTable ||
    locationTable ||
    (Array.isArray(card.details) && card.details.length) ||
    card.actions?.length ||
    (typeof card.details === 'string' && card.details)
  );
  const trendStyle = trendStyles[card.metric?.trend] || trendStyles.neutral;
  const accentStyle = {
    borderLeft: `3px solid ${trendStyle.fg}`,
  };

  return (
    <div className={`briefing-card ${expanded ? 'is-expanded' : ''}`} style={accentStyle}>
      <button
        type="button"
        className="briefing-card-header"
        onClick={() => hasDetails && setExpanded((v) => !v)}
        disabled={!hasDetails}
      >
        <div className="briefing-card-header-left">
          <span className="briefing-card-title">{card.title}</span>
        </div>
        {hasDetails && (
          <span className="briefing-card-chevron">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        )}
      </button>

      {card.narrative && (
        <p className="briefing-card-narrative">
          {renderNarrative(card.narrative, card.metric)}
        </p>
      )}

      {expanded && hasDetails && (
        <div className="briefing-card-body">
          {hasLocationView && (
            <div className="briefing-view-toggle">
              <button
                type="button"
                className={`briefing-view-toggle-btn ${viewMode === 'portfolio' ? 'is-active' : ''}`}
                onClick={() => setViewMode('portfolio')}
              >
                Portfolio
              </button>
              <button
                type="button"
                className={`briefing-view-toggle-btn ${viewMode === 'location' ? 'is-active' : ''}`}
                onClick={() => setViewMode('location')}
              >
                Location
              </button>
            </div>
          )}

          {activeTable && (
            <ExpandableAIChatTable
              key={viewMode}
              headerRow={activeTable.headerRow}
              dataRows={activeTable.dataRows}
              totalRows={activeTable.totalRows}
              headerCount={activeTable.headerCount}
              numericCols={activeTable.numericCols}
              tableMarkdown={activeTable.tableMarkdown}
              hotelName={hotelName || 'Portfolio'}
              isDarkMode={isDarkMode}
            />
          )}

          {!portfolioTable && !locationTable && Array.isArray(card.details) && card.details.length > 0 && (
            <div className="briefing-card-details">
              {card.details.map((d, i) => (
                <div key={i} className="briefing-detail-row">
                  <span className="briefing-detail-label">{d.label}</span>
                  <span className="briefing-detail-value">{d.value}</span>
                </div>
              ))}
            </div>
          )}

          {!portfolioTable && !locationTable && typeof card.details === 'string' && card.details && (
            <p className="briefing-card-details-text">{card.details}</p>
          )}

          <CardActions actions={card.actions} onPromptClick={onPromptClick} />
        </div>
      )}
    </div>
  );
}

const BriefingCardsRenderer = ({ data, onPromptClick, hotelName, isDarkMode }) => {
  if (!data || !Array.isArray(data.cards)) {
    return <p className="chart-parse-error">Invalid briefing data</p>;
  }

  return (
    <div className="briefing-cards-container">
      {data.header && (
        <div className="briefing-header">
          {data.header.title && <h3 className="briefing-header-title">{data.header.title}</h3>}
          {data.header.subtitle && <p className="briefing-header-subtitle">{data.header.subtitle}</p>}
        </div>
      )}

      <div className="briefing-cards-stack">
        {data.cards.map((card) => (
          <BriefingCard
            key={card.id || card.title}
            card={card}
            onPromptClick={onPromptClick}
            hotelName={hotelName}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>
    </div>
  );
};

export default BriefingCardsRenderer;
